import { useEffect, useMemo, useRef, useState } from "react";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { GraphQLError } from "graphql";
import { ClientError, RequestDocument, RequestOptions, Variables } from "graphql-request";
import { VariablesAndRequestHeadersArgs } from "graphql-request/build/legacy/helpers/types";
import { createClient, ExecutionResult, Sink } from "graphql-ws";
import useDeepCompareEffect from "use-deep-compare-effect";

import { handlePermissionError } from "lib/graphqlErrors";
import { isAuthenticationError } from "lib/queryRetry";
import { useProvidedAppToken } from "../../providers/ApiTokenProvider";
import { useProvidedGraphQlClient } from "../../providers/GraphQlClientProvider";
import { websocketUrl } from "./graphQlClientFactory";
import { APP_TOKEN_QUERY_KEY, isTokenExpired } from "./useAppToken";

// The authed GraphQL client is created once by GraphQlClientProvider and shared via context, so
// components and route loaders use the same instance with no module-level state. It reads the
// current app token per request, so it stays valid across token refreshes.
export function useGraphQlClient() {
  const client = useProvidedGraphQlClient();
  // Distinguishes a signed-in user whose token was rejected from a guest who never had one — they
  // need different handling for the same UNAUTHENTICATED response. Read from Rails rather than
  // from the token, which is also empty once the session behind it has gone.
  const isSignedIn = window.userSignedIn !== false;
  const queryClient = useQueryClient();
  return { client, isReady: true, isSignedIn, queryClient };
}

// Default per-query timeout, sat just under the 60s CloudFront origin read
// timeout on app.stotles.com. Anything slower is 504'd by the gateway anyway,
// so we abort a touch earlier for a clean, fast, cancellable failure instead of
// an indefinite spinner. Callers can override via `timeoutMs`. Queries only —
// mutations are deliberately not timed out (a client abort can't roll back a
// server-side write).
const DEFAULT_QUERY_TIMEOUT_MS = 58_000;

export function useGraphQlQuery<T, V extends Variables = Variables>(
  key: QueryKey,
  document: RequestDocument | TypedDocumentNode<T, V>,
  variables: VariablesAndRequestHeadersArgs<V>,
  options?: UseQueryOptions<T, unknown, T, QueryKey> & { timeoutMs?: number },
) {
  const { client, isReady, isSignedIn, queryClient } = useGraphQlClient();
  const { timeoutMs = DEFAULT_QUERY_TIMEOUT_MS, ...queryOptions } = options ?? {};
  const query = useQuery(
    key,
    () => {
      // `AbortSignal.timeout` aborts the underlying fetch when it fires (so we
      // don't hold the connection open) and rejects with a `TimeoutError`.
      const [requestVariables, requestHeaders] = variables;
      const requestOptions: RequestOptions<Variables, T> = {
        document,
        variables: requestVariables,
        requestHeaders,
        signal: AbortSignal.timeout(timeoutMs),
      };
      return client.request(requestOptions);
    },
    {
      ...queryOptions,
      enabled: isReady && (queryOptions.enabled ?? true),
    },
  );

  if (query.error) {
    handleGraphQlError(query.error as ClientError, isSignedIn, queryClient);
  }

  return query;
}

export function useGraphQlInfiniteQuery<T, V extends Variables = Variables>(
  key: QueryKey,
  document: RequestDocument | TypedDocumentNode<T, V>,
  queryFn: (page: number) => VariablesAndRequestHeadersArgs<V>,
  options?: UseInfiniteQueryOptions<T, unknown, T, T, QueryKey>,
): UseInfiniteQueryResult<T, unknown> {
  const { client, isSignedIn, queryClient } = useGraphQlClient();

  const inifiniteQuery = useInfiniteQuery<T>(
    key,
    async ({ pageParam = 1 }) => {
      const vars = queryFn(pageParam);
      return client.request(document, ...vars);
    },
    options,
  );

  if (inifiniteQuery.error) {
    handleGraphQlError(inifiniteQuery.error as ClientError, isSignedIn, queryClient);
  }
  return inifiniteQuery;
}

// `C` is the context `onMutate` returns, e.g. the data to roll back to if the mutation fails
export function useGraphQlMutation<T, V extends Variables = Variables, C = unknown>(
  document: TypedDocumentNode<T, V>,
  options?: UseMutationOptions<T, ClientError, V, C>,
): UseMutationResult<T, ClientError, V, C> {
  const { client, isSignedIn, queryClient } = useGraphQlClient();

  const mutation = useMutation(async (variables: V) => {
    const vars = [variables] as unknown as VariablesAndRequestHeadersArgs<V>;
    return client.request(document, ...vars);
  }, options);

  if (mutation.error) {
    handleGraphQlError(mutation.error, isSignedIn, queryClient);
  }
  return mutation;
}

export type UseSubscriptionOptions<D> = {
  enabled?: boolean;
  onData?: (data: D, errors?: GraphQLError[]) => void;
};

export function useSubscription<D, V extends Record<string, unknown>>(
  query: string,
  variables: V,
  options?: UseSubscriptionOptions<D>,
) {
  const token = useProvidedAppToken();
  const [data, setData] = useState<D>();
  const [errors, setErrors] = useState<GraphQLError[]>([]);
  const isEnabled = options?.enabled ?? true;

  // Keep the latest onData callback without forcing resubscribe on every render.
  const onDataRef = useRef<UseSubscriptionOptions<D>["onData"]>();
  useEffect(() => {
    onDataRef.current = options?.onData;
  }, [options?.onData]);

  const client = useMemo(() => {
    return createClient({
      url: websocketUrl.toString(),
      // authorization must be lower case for graphql-ws
      connectionParams: () => {
        // Check if token is expired before attempting connection
        // This prevents reconnection attempts with stale tokens when tabs are resumed
        if (!token || isTokenExpired(token)) {
          return {}; // Return empty params to prevent connection with expired token
        }
        return { authorization: `Bearer ${token}` };
      },
      keepAlive: 25_000,
      retryAttempts: 5,
      retryWait: async (retries) => {
        // exponential backoff: 1s, 2s, 4s, 8s, 16s
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, retries) * 1000));
      },
      shouldRetry: () => {
        // Only retry if token is still valid
        // This prevents excessive retries with expired tokens
        return !!token && !isTokenExpired(token);
      },
    });
  }, [token]);

  const sink: Sink<ExecutionResult<Record<string, unknown>, unknown>> = useMemo(() => {
    return {
      next: (result) => {
        setData(result.data as D);
        const nextErrors: GraphQLError[] | undefined = result.errors
          ? [...result.errors]
          : undefined;
        if (nextErrors) {
          setErrors(nextErrors);
        }
        onDataRef.current?.(result.data as D, nextErrors);
      },
      error: (error) => {
        setErrors((err) => [...err, error as GraphQLError]);
      },
      complete: () => {},
    };
  }, []);

  // reset state & starts the subscription if it's enabled
  useDeepCompareEffect(() => {
    setData(undefined);
    setErrors([]);
    let unsubscribe: (() => void) | undefined;

    // Only subscribe if we have a valid (non-expired) token
    // This prevents subscription attempts when returning to tabs with expired tokens
    // When token is refreshed (e.g., via refetchOnWindowFocus), this effect re-runs
    // and automatically reconnects with the new valid token
    if (token && !isTokenExpired(token) && isEnabled) {
      unsubscribe = client.subscribe({ query, variables }, sink);
    }

    return () => {
      unsubscribe?.();
    };
  }, [query, variables, token, isEnabled, client]);

  return { data, isError: errors.length > 0, errors };
}

// Both bounds have to survive a page reload to bound anything, so they live in sessionStorage
// rather than module state.
const AUTH_RECOVERY_AT_KEY = "graphQlAuthRecoveryAt";
const AUTH_RELOAD_AT_KEY = "graphQlAuthReloadAt";
// This handler runs on every render while a query sits in its error state, and the several queries
// a save refetches each report their own failure — so one attempt covers a burst. A minute is
// comfortably below the 10-minute token lifetime, so it can never swallow a genuine second expiry,
// and it keeps the attempts sparse if app-service is rejecting every token for a while.
const AUTH_RECOVERY_DEBOUNCE_MS = 60_000;
// A reload is destructive — it discards whatever the user was part-way through — so cap how often
// one tab can do it. Without a cap, an app-service that rejects every token (a key rotation, clock
// skew, a bad deploy) reloads every tab in a loop for as long as it lasts.
const AUTH_RELOAD_MIN_INTERVAL_MS = 600_000;

function hasRejectedQuery(queryClient: QueryClient): boolean {
  return queryClient
    .getQueryCache()
    .findAll({ type: "active" })
    .some((query) => isAuthenticationError(query.state.error));
}

/**
 * Reload so Rails' `authenticate_user!` can bounce the user to sign-in (`Devise::CustomFailure`
 * redirects to /auth/login with the current path as `redirect`).
 */
function reloadRejectedSession() {
  const lastReloadAt = Number(sessionStorage.getItem(AUTH_RELOAD_AT_KEY));
  if (Date.now() - lastReloadAt < AUTH_RELOAD_MIN_INTERVAL_MS) {
    return;
  }
  sessionStorage.setItem(AUTH_RELOAD_AT_KEY, String(Date.now()));
  window.location.reload();
}

/**
 * A rejected token is usually just an expired one, so re-mint it and re-run what failed — requests
 * resolve their authorization header per request, so the refetch carries the new token. Whether to
 * escalate is decided by how that turns out, not on a timer: queries still rejected once they have
 * run again with a fresh token mean the Rails session is gone, and only a reload fixes that.
 */
function recoverRejectedSession(queryClient: QueryClient) {
  const lastRecoveryAt = Number(sessionStorage.getItem(AUTH_RECOVERY_AT_KEY));
  if (Date.now() - lastRecoveryAt < AUTH_RECOVERY_DEBOUNCE_MS) {
    return;
  }
  sessionStorage.setItem(AUTH_RECOVERY_AT_KEY, String(Date.now()));

  void queryClient
    .refetchQueries({ queryKey: APP_TOKEN_QUERY_KEY })
    .then(() =>
      queryClient.refetchQueries({
        type: "active",
        predicate: (query) => isAuthenticationError(query.state.error),
      }),
    )
    .then(() => {
      if (hasRejectedQuery(queryClient)) {
        reloadRejectedSession();
      }
    });
}

/**
 * Central handling for the auth/permission failures worth telling the user about. Anything else
 * is left to the caller's own error state.
 */
function handleGraphQlError(error: ClientError, isSignedIn: boolean, queryClient: QueryClient) {
  // Only signed-in users can have a session to recover. Guests (public report / lead URLs) send no
  // token at all, so an UNAUTHENTICATED response there means the data needs an account — leave
  // that to the caller's own error state. Rails tells us which we are: `userSignedIn` is only
  // `false` for a logged-out visitor (it is absent entirely on the admin entry points).
  if (isSignedIn && isAuthenticationError(error)) {
    recoverRejectedSession(queryClient);
    return;
  }

  handlePermissionError(error);
}
